import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { api, downloadFile } from "@/api";
import { Copy, FileSpreadsheet, Printer, ListChecks, Share2, Mail, MessageCircle, Search, Download } from "lucide-react";
import { toast } from "sonner";
import { formatDateIT } from "@/lib/utils";

function PersonnelSearch({ value, onChange }) {
  return (
    <div className="relative">
      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        data-testid="personnel-search-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") { onChange(""); e.target.blur(); } }}
        placeholder="Cerca nominativo...  ( / )"
        title="Filtra ed evidenzia i nominativi. Scorciatoia: / — Esc per cancellare"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="pl-7 pr-2 py-1.5 text-sm border border-slate-300 rounded-sm w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-blue-200"
      />
    </div>
  );
}

function DayTitle({ data, stats }) {
  return (
    <div>
      <h2 data-testid="day-title" className="text-2xl font-black tracking-tighter text-slate-900">
        {data.weekday} <span className="font-mono">{formatDateIT(data.date_us)}</span>
      </h2>
      <p className="text-xs font-mono font-semibold text-slate-600">
        Base: {stats.base_total} · Assegnati univoci: {stats.total_unique} · BIS: {stats.bis.length}
      </p>
    </div>
  );
}

function CopyPanel({ months, currentMonthKey, dayNum, onCopy }) {
  const [srcMonth, setSrcMonth] = useState(currentMonthKey);
  const [srcDay, setSrcDay] = useState(Math.max(1, dayNum - 1));
  const srcNumDays = (months.find((m) => m.key === srcMonth) || {}).num_days || 1;
  const dayOptions = useMemo(
    () => Array.from({ length: srcNumDays }, (_, i) => i + 1).filter((d) => !(srcMonth === currentMonthKey && d === dayNum)),
    // _, i, d sono parametri delle callback: le dipendenze reali sono sotto
    [srcNumDays, srcMonth, currentMonthKey, dayNum] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const effectiveDay = dayOptions.includes(srcDay) ? srcDay : (dayOptions[0] ?? 1);
  return (
    <div data-testid="copy-day-panel"
      className="absolute right-0 mt-1 z-50 bg-white border border-slate-300 rounded-sm shadow-lg p-3 space-y-2 w-72">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-600 whitespace-nowrap w-14">Mese</span>
        <select data-testid="copy-source-month" value={srcMonth} onChange={(e) => setSrcMonth(e.target.value)}
          className="flex-1 text-sm border border-slate-300 rounded-sm px-2 py-1 bg-white">
          {months.map((m) => <option key={m.key} value={m.key}>{m.key}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-600 whitespace-nowrap w-14">Giorno</span>
        <select data-testid="copy-source-select" value={effectiveDay} onChange={(e) => setSrcDay(Number(e.target.value))}
          className="flex-1 text-sm border border-slate-300 rounded-sm px-2 py-1 bg-white">
          {dayOptions.map((d) => (
            <option key={d} value={d}>{String(d).padStart(2, "0")}</option>
          ))}
        </select>
        <Button data-testid="copy-confirm-button" size="sm"
          className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white shrink-0"
          onClick={() => onCopy(effectiveDay, srcMonth)}>
          Copia
        </Button>
      </div>
    </div>
  );
}

function SharePanel({ monthKey, dayNum }) {
  const [busy, setBusy] = useState(false);
  const [yy, mm] = monthKey.split(" ");
  const fname = `Giornaliera ${yy} ${mm} ${String(dayNum).padStart(2, "0")}`;
  const pdfPath = `/months/${encodeURIComponent(monthKey)}/days/${dayNum}/export-pdf`;
  const message = `Programmazione del giorno allegata: file PDF "${fname}" (scaricato dall'app).`;

  const downloadPdf = async () => {
    setBusy(true);
    try {
      await downloadFile(pdfPath, `${fname}.pdf`);
      toast.success(`PDF "${fname}.pdf" scaricato`);
    } catch {
      toast.error("Errore durante la generazione del PDF: riprova");
    } finally {
      setBusy(false);
    }
  };

  const shareNative = async () => {
    setBusy(true);
    try {
      const res = await api.get(pdfPath, { responseType: "blob" });
      const file = new File([res.data], `${fname}.pdf`, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fname });
      } else {
        await downloadFile(pdfPath, `${fname}.pdf`);
        toast.info("Condivisione diretta non supportata da questo browser: PDF scaricato, allegalo a mail o WhatsApp");
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Errore condivisione PDF: riprova");
    } finally {
      setBusy(false);
    }
  };

  // download in background senza bloccare l'apertura di mail/WhatsApp
  const downloadInBackground = () => {
    downloadFile(pdfPath, `${fname}.pdf`)
      .then(() => toast.success(`PDF "${fname}.pdf" scaricato`))
      .catch(() => toast.error("Errore download PDF: riprova"));
  };

  const linkCls = "flex items-center gap-2 w-full text-left px-2 py-1.5 text-sm rounded-sm border border-slate-200 hover:bg-slate-100 transition-colors disabled:opacity-50";
  return (
    <div data-testid="share-day-panel"
      className="absolute right-0 mt-1 z-50 bg-white border border-slate-300 rounded-sm shadow-lg p-3 space-y-2 w-64 max-w-[calc(100vw-2rem)]">
      <button data-testid="share-native-button" onClick={shareNative} disabled={busy} className={linkCls}>
        <Share2 className="w-4 h-4 text-slate-600" /> {busy ? "Preparazione PDF..." : "Condividi PDF"}
      </button>
      <button data-testid="share-download-button" onClick={downloadPdf} disabled={busy} className={linkCls}>
        <Download className="w-4 h-4 text-slate-600" /> {busy ? "Preparazione PDF..." : "Scarica PDF"}
      </button>
      <a data-testid="share-email-link" className={linkCls}
        href={`mailto:?subject=${encodeURIComponent(fname)}&body=${encodeURIComponent(message)}`}
        onClick={downloadInBackground}>
        <Mail className="w-4 h-4 text-slate-600" /> Invia via Email
      </a>
      <a data-testid="share-whatsapp-link" className={linkCls} target="_blank" rel="noreferrer"
        href={`https://wa.me/?text=${encodeURIComponent(message)}`}
        onClick={downloadInBackground}>
        <MessageCircle className="w-4 h-4 text-slate-600" /> Invia via WhatsApp
      </a>
      <p className="text-[11px] text-slate-500">Il PDF richiede qualche secondo di generazione. Da smartphone usa "Condividi PDF" e scegli WhatsApp o Email dal menu di sistema.</p>
    </div>
  );
}

export default function DayHeader({ data, stats, monthKey, dayNum, numDays, months, onCopyPrevious, onEditBaseList, personFilter, onPersonFilterChange }) {
  const [showCopy, setShowCopy] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const exportDay = () =>
    downloadFile(`/months/${encodeURIComponent(monthKey)}/days/${dayNum}/export`,
      `${monthKey} - giorno ${String(dayNum).padStart(2, "0")}.xlsx`)
      .then(() => toast.success("Export giorno scaricato"));

  const printA3 = () => {
    document.body.classList.add("print-a3");
    window.addEventListener("afterprint", () => document.body.classList.remove("print-a3"), { once: true });
    window.print();
  };

  return (
    <div className="print-day-header px-4 pt-4 pb-2 flex items-center gap-3 flex-wrap">
      <DayTitle data={data} stats={stats} />
      <div className="print-hide ml-auto flex items-center gap-2 flex-wrap">
        <PersonnelSearch value={personFilter} onChange={onPersonFilterChange} />
        {numDays > 1 && (
          <div className="relative">
            <Button data-testid="copy-previous-button" size="sm" onClick={() => setShowCopy((v) => !v)}
              className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white">
              <Copy className="w-4 h-4 mr-1" /> Copia programmazione
            </Button>
            {showCopy && (
              <CopyPanel months={months} currentMonthKey={monthKey} dayNum={dayNum}
                onCopy={(src, srcM) => { onCopyPrevious(false, src, srcM); setShowCopy(false); }} />
            )}
          </div>
        )}
        <Button data-testid="edit-base-list-button" size="sm" variant="outline" className="rounded-sm" onClick={onEditBaseList}>
          <ListChecks className="w-4 h-4 mr-1" /> Lista base
        </Button>
        <Button data-testid="export-day-button" size="sm" variant="outline" className="rounded-sm" onClick={exportDay}>
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Esporta giorno
        </Button>
        <div className="relative">
          <Button data-testid="share-day-button" size="sm" variant="outline" className="rounded-sm"
            onClick={() => setShowShare((v) => !v)}>
            <Share2 className="w-4 h-4 mr-1" /> Condividi giorno
          </Button>
          {showShare && <SharePanel monthKey={monthKey} dayNum={dayNum} />}
        </div>
        <Button data-testid="print-button" size="sm" variant="outline" className="rounded-sm" onClick={() => window.print()}>
          <Printer className="w-4 h-4 mr-1" /> Stampa A4
        </Button>
        <Button data-testid="print-a3-button" size="sm" variant="outline" className="rounded-sm" onClick={printA3}
          title="Stampa in formato A3 verticale con 4 colonne di cantieri">
          <Printer className="w-4 h-4 mr-1" /> Stampa A3
        </Button>
      </div>
    </div>
  );
}
